import React, { useEffect, useRef } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/layout/Header.jsx';
import Footer from './components/layout/Footer.jsx';
import Landing from './pages/Landing.jsx';
import FeaturePage from './pages/FeaturePage.jsx';
import AboutPage from './pages/AboutPage.jsx';
import NotFound from './pages/NotFound.jsx';
import GroupsHeader from './groups/Header.jsx';
import GroupsFooter from './groups/Footer.jsx';
import GroupsLanding from './groups/Landing.jsx';
import groupsI18n from './i18n/config.js';
import smbI18n from './i18n/smb.js';
import { FEATURES, SMB_BASE } from './content/features.js';
import { ABOUT_PATH, LEGACY_REDIRECTS } from './content/site.js';
import './App.css';

/**
 * Chrome for the site at /smb: its own copy, header and footer.
 * @returns {JSX.Element} SMB layout
 */
function SmbLayout() {
  return (
    <I18nextProvider i18n={smbI18n}>
      <div className="page smb">
        <Header />
        <main className="page-main">
          <Outlet />
        </main>
        <Footer />
      </div>
    </I18nextProvider>
  );
}

/**
 * Chrome for the groups page at /.
 * @returns {JSX.Element} Groups layout
 */
function GroupsLayout() {
  return (
    <I18nextProvider i18n={groupsI18n}>
      <div className="page">
        <GroupsHeader />
        <main className="page-main">
          <Outlet />
        </main>
        <GroupsFooter />
      </div>
    </I18nextProvider>
  );
}

/**
 * Sends an old SMB path to its /smb URL, keeping the query and hash.
 * @param {Object} props
 * @param {string} props.to - New path
 * @returns {JSX.Element} Redirect
 */
function LegacyRedirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/**
 * Root layout. `/` is the groups page. `/smb` is the site for single companies.
 * Old SMB paths redirect under `/smb`.
 * @returns {JSX.Element} Application routes
 */
function App() {
  const { pathname, hash, key } = useLocation();
  const previousPathname = useRef(pathname);

  // Start each new page at the top. If the link points to a section (e.g. /smb/om-oss#kontakt),
  // go to that section instead: straight there on a new page, gliding when already on the page.
  useEffect(() => {
    const isNewPage = previousPathname.current !== pathname;
    previousPathname.current = pathname;
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;

    if (target) {
      target.scrollIntoView({ behavior: isNewPage ? 'instant' : 'auto' });
    } else if (isNewPage) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [pathname, hash, key]);

  const smbPage = (path) => path.slice(SMB_BASE.length + 1);

  return (
    <Routes>
      <Route path={SMB_BASE} element={<SmbLayout />}>
        <Route index element={<Landing />} />
        {FEATURES.map((feature) => (
          <Route
            key={feature.id}
            path={smbPage(feature.path)}
            element={<FeaturePage feature={feature} />}
          />
        ))}
        <Route path={smbPage(ABOUT_PATH)} element={<AboutPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      {LEGACY_REDIRECTS.map((redirect) => (
        <Route
          key={redirect.from}
          path={redirect.from}
          element={<LegacyRedirect to={redirect.to} />}
        />
      ))}
      <Route element={<GroupsLayout />}>
        <Route path="/" element={<GroupsLanding />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default App;
